// ═══ 말소대행신청 대당 확인 카드 (B3 S2 · Final2 ③) + 차량별 예외 시트 + 부가서비스 칩 ═══
// 웹은 칸을 카드 안에서 바로 고친다. 노란 칸 = requestLogic.flaggedFields(OCR 약한 칸 · 빈 필수 칸 · 주민번호 13자리 아님).
// 말소신청서에 찍히는 칸(REQUIRED_FIELDS)이 거의 전부라 필수 표시는 없고, 선택 칸(차종·등록일)에만 '(선택)' (Jin 10/28).
// 주민/법인번호는 OCR 앞 6자리를 고정 표시하고 뒷자리만 입력(OCR 실패 카드는 앞자리도 입력).
// 부가서비스 칩은 직접 렌더(ExtraServicePicker 는 한글 고정이라 재사용하지 않는다).
import React, { useState } from 'react';
import { T, R, S, FONT } from '../../theme';
import ConfirmSheet from '../../simple/parts/ConfirmSheet';
import { input as inputSt } from '../../simple/screens/request/bits';
import { REQUIRED_FIELDS, flaggedFields, isPdf, missingRequired, normalizeVin, serviceEntry } from '../requestLogic';

var LABEL = { carNo: 'malsoModeFieldCarNo', vin: 'malsoModeFieldVin', carType: 'cancelDetailCarType', regDate: 'malsoModeFieldRegDate',
  mileage: 'malsoModeFieldMileage', owner: 'cancelDetailOwner', ownerId: 'malsoModeFieldOwnerId', ownerAddr: 'malsoModeFieldOwnerAddr' };

var cardSt = { background: T.card, border: '1px solid ' + T.bd, borderRadius: R.card, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 };
var head = { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 };
var titleSt = { flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 800, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var pill = { flex: 'none', fontSize: FONT.badge, fontWeight: 700, padding: '3px 8px', borderRadius: R.pill };
var TONE = { ok: { background: T.okTint, color: T.ok }, warn: { background: T.warnTint, color: T.warn },
  danger: { background: T.dangerTint, color: T.danger }, muted: { background: T.track, color: T.muted } };
var xBtn = { border: 0, background: 'none', color: T.faint, fontSize: 15, cursor: 'pointer', padding: 4, fontFamily: 'inherit' };
var grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 6 };
var fld = { display: 'flex', flexDirection: 'column', gap: 4, padding: 6, borderRadius: R.md, minWidth: 0 };
var fldWarn = Object.assign({}, fld, { background: T.warnTint });
var lab = { fontSize: FONT.label, fontWeight: 700, color: T.muted };
var hint = { fontSize: 11, color: T.warn };
var idRow = { display: 'flex', alignItems: 'center', gap: 6 };
var front = { flex: 'none', fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: 'tabular-nums' };
var thumbBox = { flex: 'none', borderRadius: R.sm, background: T.track, objectFit: 'cover', display: 'grid', placeItems: 'center',
  fontSize: 10.5, fontWeight: 800, color: T.muted };
var chips = { display: 'flex', flexWrap: 'wrap', gap: 6 };
var toggleRow = { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' };
var toggleSub = { display: 'block', fontSize: 11.5, color: T.muted };

/// 등록증 썸네일 — PDF 는 글자 상자. big = 신청 ① 그리드.
export function CardThumb(props) {
  var c = props.card;
  var size = props.big ? { width: 72, height: 54 } : { width: 44, height: 33 };
  if (isPdf(c.file) || !c.url) return <span style={Object.assign({}, thumbBox, size)}>{isPdf(c.file) ? 'PDF' : ''}</span>;
  return <img src={c.url} alt="" style={Object.assign({}, thumbBox, size)} />;
}

/// 부가서비스 칩 — value = services 배열(serviceEntry 모양). props: { options, value, onChange }
export function ServiceChips(props) {
  var ids = props.value.map(function (s) { return s.serviceId; });
  return (<div style={chips}>{props.options.map(function (o) {
    var on = ids.indexOf(o.id) >= 0;
    return (<button key={o.id} type="button" style={on ? S.chipOn : S.chip} aria-pressed={on} data-svc={o.id}
      onClick={function () {
        props.onChange(on ? props.value.filter(function (s) { return s.serviceId !== o.id; }) : props.value.concat([serviceEntry(o)]));
      }}>{o.name}</button>);
  })}</div>);
}

function statusOf(t, c, n) {
  if (c.exists) return [TONE.danger, t('malsoModeErrVinExistsBadge')];
  if (c.ocr === 'reading') return [TONE.muted, t('malsoModeCaptureReading')];
  if (c.ocr === 'failed') return [TONE.warn, t('malsoModeCaptureFailed')];
  if (n > 0) return [TONE.warn, t('malsoModeCheckNeeded', { count: n })];
  return [TONE.ok, t('malsoModeCheckDone')];
}

/// props: { t, index(1부터), card, hasOverride, onEdit(field, value), onRemove, onPerCar|null }
function MalsoCarCard(props) {
  var t = props.t;
  var c = props.card;
  var flagged = flaggedFields(c);
  var missing = missingRequired(c);
  var st = statusOf(t, c, flagged.length);
  var input = function (f, extra) {
    return (<input style={inputSt} value={c[f]} data-testid={'car-' + c.key + '-' + f} {...extra}
      onChange={function (e) { props.onEdit(f, f === 'mileage' ? e.target.value.replace(/\D/g, '') : e.target.value); }} />);
  };
  // 주민/법인번호 — 앞 6자리를 다 치면 뒷칸으로(뒷칸이 비었을 때만), 앞칸에 이어 친 숫자는 뒷칸 끝에 잇는다 (Jin 11/03).
  var backRef = React.useRef(null);
  var onOwnerFront = function (e) {
    var digits = e.target.value.replace(/\D/g, '');
    var back = String(c.ownerBack || '');
    if (digits.length <= 6) {
      props.onEdit('ownerFront', e.target.value);
      if (digits.length === 6 && !back && backRef.current) backRef.current.focus();
      return;
    }
    props.onEdit('ownerFront', digits.slice(0, 6));
    props.onEdit('ownerBack', (back.replace(/\D/g, '') + digits.slice(6)).slice(0, 7));
    if (backRef.current) backRef.current.focus();
  };
  var field = function (f, control, hintKey, wide) {
    var warn = flagged.indexOf(f) >= 0;
    var base = warn ? fldWarn : fld;
    // 안내 — 칸 전용 문구가 있으면 그것, 없으면 빈 필수 칸만 '입력해 주세요'.
    var hintText = warn && hintKey ? t(hintKey) : missing.indexOf(f) >= 0 ? t('malsoModeFieldRequired') : '';
    return (<div key={f} style={wide ? Object.assign({}, base, { gridColumn: '1 / -1' }) : base} data-field={f} data-warn={warn ? '1' : '0'}>
      <span style={lab}>{t(LABEL[f]) + (REQUIRED_FIELDS.indexOf(f) >= 0 ? '' : ' ' + t('malsoModeOptional'))}</span>
      {control}
      {hintText ? <span style={hint}>{hintText}</span> : null}
    </div>);
  };
  return (<div style={cardSt} data-testid={'car-card-' + c.key}>
    <div style={head}>
      <CardThumb card={c} />
      <span style={titleSt}>{props.index + ' · ' + [c.carNo, c.carType].filter(function (v) { return String(v).trim(); }).join(' ')}</span>
      <span style={Object.assign({}, pill, st[0])} data-testid={'car-pill-' + c.key}>{st[1]}</span>
      <button type="button" style={xBtn} aria-label={t('malsoModeRemoveCar')} title={t('malsoModeRemoveCar')} onClick={props.onRemove}>✕</button>
    </div>
    <div style={grid}>
      {field('carNo', input('carNo'))}
      {field('vin', input('vin', { dir: 'ltr', onBlur: function () { props.onEdit('vin', normalizeVin(c.vin)); } }), 'malsoModeVinRequired')}
      {field('carType', input('carType'))}
      {field('regDate', input('regDate', { dir: 'ltr' }))}
      {field('mileage', input('mileage', { dir: 'ltr', inputMode: 'numeric', placeholder: 'km' }))}
      {field('owner', input('owner'))}
      {field('ownerId', (<div style={idRow} dir="ltr">
        {c.ownerFixed ? <span style={front}>{c.ownerFront}</span>
          : <input style={Object.assign({}, inputSt, { width: 84, flex: 'none' })} value={c.ownerFront} data-testid={'car-' + c.key + '-ownerFront'}
            inputMode="numeric" onChange={onOwnerFront} />}
        <span>-</span>
        {input('ownerBack', { inputMode: 'numeric', ref: backRef })}
      </div>), 'malsoModeFieldOwnerIdHint')}
      {field('ownerAddr', input('ownerAddr'), null, true)}
    </div>
    {props.onPerCar ? (<button type="button" style={Object.assign({}, S.link, { alignSelf: 'flex-start' })} data-testid={'percar-' + c.key}
      onClick={props.onPerCar}>{t(props.hasOverride ? 'malsoModePerCarOptions' : 'malsoModePerCarUseCommon')}</button>) : null}
  </div>);
}

/// 차량별 예외 — 부가서비스·바이어를 각각 '공통 옵션 사용' 또는 이 차만 따로 (앱 _PerCarSheet).
/// props: { t, card, options, exportCancel, value(override|undefined), onSave(override|null), onClose }
export function PerCarSheet(props) {
  var t = props.t;
  var v = props.value || {};
  var stSvc = useState(v.services || null); var services = stSvc[0], setServices = stSvc[1];
  var stBuyer = useState(typeof v.buyerName === 'string' ? v.buyerName : null); var buyer = stBuyer[0], setBuyer = stBuyer[1];
  var toggle = function (labelKey, common, onCommon, id) {
    return (<label style={toggleRow}>
      <span style={{ flex: 1 }}><b>{t(labelKey)}</b><span style={toggleSub}>{t('malsoModePerCarUseCommon')}</span></span>
      <input type="checkbox" checked={common} data-testid={id} onChange={function (e) { onCommon(e.target.checked); }} />
    </label>);
  };
  var save = function () {
    var ov = {};
    if (services && props.options.length) ov.services = services;
    if (buyer !== null && props.exportCancel) ov.buyerName = buyer.trim();
    props.onSave(Object.keys(ov).length ? ov : null);
  };
  var c = props.card;
  return (<ConfirmSheet open title={t('malsoModePerCarOptions')} sub={[c.carNo, normalizeVin(c.vin)].filter(Boolean).join(' · ')}
    onConfirm={save} onClose={props.onClose}>
    {props.options.length ? toggle('extraSvcTitle', !services, function (on) { setServices(on ? null : []); }, 'percar-svc-common') : null}
    {props.options.length && services ? <ServiceChips options={props.options} value={services} onChange={setServices} /> : null}
    {props.exportCancel ? toggle('buyersTitle', buyer === null, function (on) { setBuyer(on ? null : ''); }, 'percar-buyer-common') : null}
    {props.exportCancel && buyer !== null ? (<input style={inputSt} list="malso-buyers" value={buyer} data-testid="percar-buyer"
      placeholder={t('malsoModeBuyerHint')} onChange={function (e) { setBuyer(e.target.value); }} />) : null}
  </ConfirmSheet>);
}

export default MalsoCarCard;
